import React, { useState, useEffect, useMemo } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { tripAPI, FlexibleTripRequest } from '../../api/trips';
import { vehicleAPI } from '../../api/vehicles';
import { supplierAPI } from '../../api/suppliers';
import { customerAPI } from '../../api/customers';
import { driverAPI } from '../../api/drivers';
import { itemAPI } from '../../api/items';
import { warehouseAPI } from '../../api/warehouses';
import { Vehicle, Supplier, Customer, Driver, Item, Warehouse } from '../../types/entities';
import { Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  SearchableCombobox,
  createVehicleOptions,
  createDriverOptions,
  createItemOptions,
} from '../../components/UI/SearchableCombobox';
import { TripTypeSelector, PickupDeliverySection, EntityType, TripItemsTable } from '../../components/Trip';
import { DocumentUpload, DocumentList } from '../../components/Documents';
import CurrencyInput from '../../components/UI/CurrencyInput';

// Shape dùng chung cho cả items, purchaseItems, deliveryItems
interface ItemRow {
  itemId?: number | null;
  itemName: string;
  volumeWeight: string;
  unit: string;
  unitPrice: string;
  purchasePrice: string;
  salePrice: string;
  isGiftFromSupplier?: boolean;
  isGiftToCustomer?: boolean;
}

interface TripFormData {
  date: string;
  vehicleId: number | null;
  driverId: number | null;
  distanceKm: string;
  driverWage: string;
  tripType: 'IMPORT' | 'EXPORT';
  pickup: {
    type: EntityType;
    id: number | null;
    address: string;
    contactPerson: string;
    contactPhone: string;
  };
  delivery: {
    type: EntityType;
    id: number | null;
    address: string;
    contactPerson: string;
    contactPhone: string;
  };
  // Chuyến không trực tiếp (IMPORT/EXPORT đơn): dùng items
  items: ItemRow[];
  // Chuyến trực tiếp SUPPLIER→CUSTOMER: dùng hai danh sách riêng
  purchaseItems: ItemRow[];  // Hàng nhập từ NCC
  deliveryItems: ItemRow[];  // Hàng giao cho KH
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('vi-VN').format(value);
};

export const TripForm: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [documentRefreshKey, setDocumentRefreshKey] = useState(0);

  // Data for dropdowns
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [catalogItems, setCatalogItems] = useState<Item[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<TripFormData>({
    defaultValues: {
      date: new Date().toISOString().split('T')[0],
      vehicleId: null,
      driverId: null,
      distanceKm: '',
      driverWage: '',
      tripType: 'IMPORT',
      pickup: {
        type: 'SUPPLIER',
        id: null,
        address: '',
        contactPerson: '',
        contactPhone: '',
      },
      delivery: {
        type: 'WAREHOUSE',
        id: null,
        address: '',
        contactPerson: '',
        contactPhone: '',
      },
      items: [{ itemId: null, itemName: '', volumeWeight: '', unit: 'M3', unitPrice: '', purchasePrice: '', salePrice: '' }],
      purchaseItems: [{ itemId: null, itemName: '', volumeWeight: '', unit: 'M3', unitPrice: '', purchasePrice: '', salePrice: '' }],
      deliveryItems: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
  });

  // Field arrays cho chuyến trực tiếp SUPPLIER→CUSTOMER
  const { fields: purchaseFields, append: appendPurchase, remove: removePurchase } = useFieldArray({
    control,
    name: 'purchaseItems',
  });
  const { fields: deliveryFields, append: appendDelivery, remove: removeDelivery } = useFieldArray({
    control,
    name: 'deliveryItems',
  });

  // Watch form values
  const tripType = watch('tripType');
  const pickup = watch('pickup');
  const delivery = watch('delivery');
  const items = watch('items');
  const purchaseItems = watch('purchaseItems');
  const deliveryItems = watch('deliveryItems');

  // Determine price mode based on pickup/delivery types
  // SUPPLIER→CUSTOMER: both purchase + sale price
  // SUPPLIER→WAREHOUSE: purchase price only
  // WAREHOUSE→CUSTOMER: sale price only
  // other: single unitPrice
  const priceMode = useMemo(() => {
    const pt = pickup?.type;
    const dt = delivery?.type;
    if (pt === 'SUPPLIER' && dt === 'CUSTOMER') return 'both';
    if (pt === 'SUPPLIER' && dt === 'WAREHOUSE') return 'purchase';
    if (pt === 'WAREHOUSE' && dt === 'CUSTOMER') return 'sale';
    return 'single';
  }, [pickup?.type, delivery?.type]);

  // Create combobox options
  const vehicleOptions = useMemo(() => createVehicleOptions(vehicles), [vehicles]);
  const driverOptions = useMemo(() => createDriverOptions(drivers), [drivers]);
  const itemOptions = useMemo(() => createItemOptions(catalogItems), [catalogItems]);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (isEdit && id) {
      loadTrip(parseInt(id));
    }
  }, [id, isEdit]);

  const loadInitialData = async () => {
    try {
      const [vehiclesData, suppliersData, customersData, driversData, itemsData, warehousesData] =
        await Promise.all([
          vehicleAPI.getAll(),
          supplierAPI.getAll(),
          customerAPI.getAll(),
          driverAPI.getAll(),
          itemAPI.getAll(),
          warehouseAPI.getAll({ status: 'ACTIVE' }),
        ]);

      setVehicles(vehiclesData.filter((v) => v.status === 'ACTIVE'));
      setSuppliers(suppliersData.filter((s) => s.status === 'ACTIVE'));
      setCustomers(customersData.filter((c) => c.status === 'ACTIVE'));
      setDrivers(driversData.filter((d) => d.status === 'ACTIVE'));
      setCatalogItems(itemsData.filter((i) => i.status === 'ACTIVE'));
      setWarehouses(warehousesData);
    } catch (error) {
      toast.error(t('errors.unknown'));
    }
  };

  const loadTrip = async (tripId: number) => {
    try {
      const trip = await tripAPI.getById(tripId);
      reset({
        date: trip.date.split('T')[0],
        vehicleId: trip.vehicleId,
        driverId: trip.driverId || null,
        distanceKm: trip.distanceKm?.toString() || '',
        driverWage: trip.driverWage?.toString() || '',
        tripType: (trip.tripType as 'IMPORT' | 'EXPORT') || 'EXPORT',
        pickup: {
          type: (trip.pickupType as EntityType) || 'SUPPLIER',
          id: trip.pickupId || trip.supplierId || null,
          address: trip.pickupAddress || '',
          contactPerson: trip.pickupContactPerson || '',
          contactPhone: trip.pickupContactPhone || '',
        },
        delivery: {
          type: (trip.deliveryType as EntityType) || 'CUSTOMER',
          id: trip.deliveryId || trip.customerId || null,
          address: trip.deliveryAddressNew || trip.deliveryAddress || '',
          contactPerson: trip.deliveryContactPerson || '',
          contactPhone: trip.deliveryContactPhone || '',
        },
        // Phân tách items theo role khi load trip để edit
        items:
          trip.items
            ?.filter((item) => !item.itemRole || item.itemRole === 'BOTH')
            .map((item) => ({
              itemId: item.itemId || null,
              itemName: item.itemName,
              volumeWeight: item.volumeWeight.toString(),
              unit: item.unit,
              unitPrice: item.unitPrice.toString(),
              purchasePrice: item.purchasePrice?.toString() || '',
              salePrice: item.salePrice?.toString() || '',
              isGiftFromSupplier: item.isGiftFromSupplier ?? false,
              isGiftToCustomer: item.isGiftToCustomer ?? false,
            })) || [],
        purchaseItems:
          trip.items
            ?.filter((item) => item.itemRole === 'PURCHASE')
            .map((item) => ({
              itemId: item.itemId || null,
              itemName: item.itemName,
              volumeWeight: item.volumeWeight.toString(),
              unit: item.unit,
              unitPrice: item.unitPrice.toString(),
              purchasePrice: item.purchasePrice?.toString() || item.unitPrice.toString(),
              salePrice: '',
              isGiftFromSupplier: item.isGiftFromSupplier ?? false,
              isGiftToCustomer: false,
            })) || [{ itemId: null, itemName: '', volumeWeight: '', unit: 'M3', unitPrice: '', purchasePrice: '', salePrice: '' }],
        deliveryItems:
          trip.items
            ?.filter((item) => item.itemRole === 'SALE')
            .map((item) => ({
              itemId: item.itemId || null,
              itemName: item.itemName,
              volumeWeight: item.volumeWeight.toString(),
              unit: item.unit,
              unitPrice: item.unitPrice.toString(),
              purchasePrice: '',
              salePrice: item.salePrice?.toString() || item.unitPrice.toString(),
              isGiftFromSupplier: item.isGiftFromSupplier ?? false,
              isGiftToCustomer: item.isGiftToCustomer ?? false,
            })) || [],
      });
    } catch (error) {
      toast.error(t('trips.loadError'));
      navigate('/trips');
    }
  };

  // Handle trip type change - set default pickup/delivery types
  const handleTripTypeChange = (type: 'IMPORT' | 'EXPORT') => {
    setValue('tripType', type);
    // Set default pickup/delivery types for the trip type.
    // Entity selections and addresses are NOT reset — user can freely change them.
    if (type === 'IMPORT') {
      setValue('pickup.type', 'SUPPLIER');
      setValue('delivery.type', 'WAREHOUSE');
    } else {
      setValue('pickup.type', 'WAREHOUSE');
      setValue('delivery.type', 'CUSTOMER');
    }
  };

  // Handle pickup type change
  const handlePickupTypeChange = (type: EntityType) => {
    setValue('pickup.type', type);
    setValue('pickup.id', null);
    setValue('pickup.address', '');
    setValue('pickup.contactPerson', '');
    setValue('pickup.contactPhone', '');
  };

  // Handle delivery type change
  const handleDeliveryTypeChange = (type: EntityType) => {
    setValue('delivery.type', type);
    setValue('delivery.id', null);
    setValue('delivery.address', '');
    setValue('delivery.contactPerson', '');
    setValue('delivery.contactPhone', '');
  };

  // Handle pickup entity selection
  const handlePickupEntitySelect = (entity: any) => {
    if (!entity) {
      setValue('pickup.id', null);
      setValue('pickup.address', '');
      setValue('pickup.contactPerson', '');
      setValue('pickup.contactPhone', '');
      return;
    }
    setValue('pickup.id', entity.id);
    setValue('pickup.address', entity.address || '');
    setValue('pickup.contactPerson', entity.contactPerson || '');
    setValue('pickup.contactPhone', entity.contactPhone || entity.phone || '');
  };

  // Handle delivery entity selection
  const handleDeliveryEntitySelect = (entity: any) => {
    if (!entity) {
      setValue('delivery.id', null);
      setValue('delivery.address', '');
      setValue('delivery.contactPerson', '');
      setValue('delivery.contactPhone', '');
      return;
    }
    setValue('delivery.id', entity.id);
    setValue('delivery.address', entity.address || '');
    setValue('delivery.contactPerson', entity.contactPerson || '');
    setValue('delivery.contactPhone', entity.contactPhone || entity.phone || '');
  };

  // Handle item selection from combobox
  const handleItemSelect = (index: number, itemId: number | string | null) => {
    if (itemId === null) {
      setValue(`items.${index}.itemId`, null);
      setValue(`items.${index}.itemName`, '');
      setValue(`items.${index}.unit`, 'M3');
      return;
    }

    const selectedItem = catalogItems.find((i) => i.id === itemId);
    if (selectedItem) {
      setValue(`items.${index}.itemId`, selectedItem.id);
      setValue(`items.${index}.itemName`, selectedItem.name);
      setValue(`items.${index}.unit`, selectedItem.defaultUnit);
    }
  };

  // Handle item selection for purchaseItems
  const handlePurchaseItemSelect = (index: number, itemId: number | string | null) => {
    if (itemId === null) {
      setValue(`purchaseItems.${index}.itemId`, null);
      setValue(`purchaseItems.${index}.itemName`, '');
      setValue(`purchaseItems.${index}.unit`, 'M3');
      return;
    }
    const selectedItem = catalogItems.find((i) => i.id === itemId);
    if (selectedItem) {
      setValue(`purchaseItems.${index}.itemId`, selectedItem.id);
      setValue(`purchaseItems.${index}.itemName`, selectedItem.name);
      setValue(`purchaseItems.${index}.unit`, selectedItem.defaultUnit);
    }
  };

  // Handle item selection for deliveryItems
  const handleDeliveryItemSelect = (index: number, itemId: number | string | null) => {
    if (itemId === null) {
      setValue(`deliveryItems.${index}.itemId`, null);
      setValue(`deliveryItems.${index}.itemName`, '');
      setValue(`deliveryItems.${index}.unit`, 'M3');
      return;
    }
    const selectedItem = catalogItems.find((i) => i.id === itemId);
    if (selectedItem) {
      setValue(`deliveryItems.${index}.itemId`, selectedItem.id);
      setValue(`deliveryItems.${index}.itemName`, selectedItem.name);
      setValue(`deliveryItems.${index}.unit`, selectedItem.defaultUnit);
    }
  };

  // Calculate totals based on price mode
  const totalPurchaseValue = items.reduce((sum, item) => {
    const volume = parseFloat(item.volumeWeight) || 0;
    const price = parseFloat(item.purchasePrice || item.unitPrice) || 0;
    return sum + volume * price;
  }, 0);

  const totalSaleValue = items.reduce((sum, item) => {
    const volume = parseFloat(item.volumeWeight) || 0;
    const price = parseFloat(item.salePrice || item.unitPrice) || 0;
    return sum + volume * price;
  }, 0);

  const totalValue = priceMode === 'purchase' ? totalPurchaseValue
    : priceMode === 'sale' ? totalSaleValue
    : priceMode === 'both' ? totalSaleValue  // display sale value as main total
    : items.reduce((sum, item) => {
        const volume = parseFloat(item.volumeWeight) || 0;
        const price = parseFloat(item.unitPrice) || 0;
        return sum + volume * price;
      }, 0);

  const onSubmit = async (data: TripFormData) => {
    // Validate required fields
    if (!data.vehicleId) {
      toast.error(t('validation.required'));
      return;
    }

    if (!data.pickup.id || !data.delivery.id) {
      toast.error(t('trips.selectPickupDelivery'));
      return;
    }

    const isDirectTrip = data.pickup.type === 'SUPPLIER' && data.delivery.type === 'CUSTOMER';

    // Validate items theo loại chuyến
    if (isDirectTrip) {
      // Chuyến trực tiếp: validate purchaseItems (bắt buộc), deliveryItems (tùy chọn)
      if (data.purchaseItems.length === 0 || !data.purchaseItems[0].itemName) {
        toast.error('Danh sách hàng nhập từ NCC không được rỗng');
        return;
      }
      for (const item of data.purchaseItems) {
        if (!item.itemName) continue;
        // Skip price validation for gift items (NCC tặng → purchasePrice = 0)
        if (!item.isGiftFromSupplier && !item.purchasePrice) {
          toast.error(t('trips.purchasePrice') + ': ' + t('validation.required'));
          return;
        }
      }
      for (const item of data.deliveryItems) {
        if (!item.itemName) continue;
        // Skip price validation for gift items (Tặng KH → salePrice = 0)
        if (!item.isGiftToCustomer && !item.salePrice) {
          toast.error(t('trips.salePrice') + ': ' + t('validation.required'));
          return;
        }
      }

      // Validate: delivery qty ≤ purchase qty per item (physical quantity, gift or not)
      if (data.deliveryItems.some(i => i.itemName)) {
        const purchaseQtyMap = new Map<string, number>();
        for (const item of data.purchaseItems) {
          const key = item.itemName?.trim();
          if (key) purchaseQtyMap.set(key, (purchaseQtyMap.get(key) || 0) + (parseFloat(item.volumeWeight) || 0));
        }
        for (const item of data.deliveryItems) {
          const key = item.itemName?.trim();
          if (!key) continue;
          const purchaseQty = purchaseQtyMap.get(key) || 0;
          const deliveryQty = parseFloat(item.volumeWeight) || 0;
          if (deliveryQty > purchaseQty + 0.001) {
            toast.error(`${t('trips.deliveryQtyExceedsPurchase', { item: key, delivery: deliveryQty, purchase: purchaseQty })}`);
            return;
          }
        }
      }
    } else {
      // Chuyến nhập kho / giao hàng: validate items thông thường
      if (data.items.length === 0 || !data.items[0].itemName) {
        toast.error(t('validation.required'));
        return;
      }
      const currentPickupType = data.pickup.type;
      const currentDeliveryType = data.delivery.type;
      const currentPriceMode =
        currentPickupType === 'SUPPLIER' && currentDeliveryType === 'WAREHOUSE' ? 'purchase'
        : currentPickupType === 'WAREHOUSE' && currentDeliveryType === 'CUSTOMER' ? 'sale'
        : 'single';

      for (const item of data.items) {
        if (!item.itemName) continue;
        if (currentPriceMode === 'purchase' && !item.isGiftFromSupplier && !item.purchasePrice) {
          toast.error(t('trips.purchasePrice') + ': ' + t('validation.required'));
          return;
        }
        if (currentPriceMode === 'sale' && !item.isGiftToCustomer && !item.salePrice) {
          toast.error(t('trips.salePrice') + ': ' + t('validation.required'));
          return;
        }
        if (currentPriceMode === 'single' && !item.unitPrice) {
          toast.error(t('trips.unitPrice') + ': ' + t('validation.required'));
          return;
        }
      }
    }

    setLoading(true);
    try {
      const basePayload = {
        date: data.date,
        vehicleId: data.vehicleId,
        driverId: data.driverId || undefined,
        distanceKm: data.distanceKm ? parseFloat(data.distanceKm) : undefined,
        driverWage: data.driverWage ? parseFloat(data.driverWage) : undefined,
        tripType: data.tripType,
        pickup: {
          type: data.pickup.type,
          id: data.pickup.id!,
          address: data.pickup.address || undefined,
          contactPerson: data.pickup.contactPerson || undefined,
          contactPhone: data.pickup.contactPhone || undefined,
        },
        delivery: {
          type: data.delivery.type,
          id: data.delivery.id!,
          address: data.delivery.address || undefined,
          contactPerson: data.delivery.contactPerson || undefined,
          contactPhone: data.delivery.contactPhone || undefined,
        },
      };

      let payload: FlexibleTripRequest;

      if (isDirectTrip) {
        // Chuyến trực tiếp: gửi 2 danh sách riêng biệt
        payload = {
          ...basePayload,
          purchaseItems: data.purchaseItems
            .filter((item) => item.itemName)
            .map((item) => {
              // Gift from supplier: purchasePrice = 0 (handled by backend override)
              const purchasePrice = item.isGiftFromSupplier ? 0 : parseFloat(item.purchasePrice);
              return {
                itemId: item.itemId || undefined,
                itemName: item.itemName,
                volumeWeight: parseFloat(item.volumeWeight),
                unit: item.unit,
                unitPrice: purchasePrice,
                purchasePrice,
                isGiftFromSupplier: item.isGiftFromSupplier ?? false,
              };
            }),
          deliveryItems: data.deliveryItems
            .filter((item) => item.itemName)
            .map((item) => {
              // Gift to customer: salePrice = 0 (handled by backend override)
              const salePrice = item.isGiftToCustomer ? 0 : parseFloat(item.salePrice);
              return {
                itemId: item.itemId || undefined,
                itemName: item.itemName,
                volumeWeight: parseFloat(item.volumeWeight),
                unit: item.unit,
                unitPrice: salePrice,
                salePrice,
                isGiftToCustomer: item.isGiftToCustomer ?? false,
              };
            }),
        };
      } else {
        // Chuyến nhập kho / giao hàng: dùng items thông thường
        payload = {
          ...basePayload,
          items: data.items.map((item) => {
            const purchasePrice = item.isGiftFromSupplier ? 0
              : item.purchasePrice ? parseFloat(item.purchasePrice) : undefined;
            const salePrice = item.isGiftToCustomer ? 0
              : item.salePrice ? parseFloat(item.salePrice) : undefined;
            const unitPrice = salePrice ?? purchasePrice ?? parseFloat(item.unitPrice);
            return {
              itemId: item.itemId || undefined,
              itemName: item.itemName,
              volumeWeight: parseFloat(item.volumeWeight),
              unit: item.unit,
              unitPrice,
              purchasePrice,
              salePrice,
              isGiftFromSupplier: item.isGiftFromSupplier ?? false,
              isGiftToCustomer: item.isGiftToCustomer ?? false,
            };
          }),
        };
      }

      if (isEdit && id) {
        await tripAPI.update(parseInt(id), payload as any);
        toast.success(t('trips.updateSuccess'));
        navigate(`/trips/${id}`);
      } else {
        const newTrip = await tripAPI.createFlexible(payload);
        toast.success(t('trips.createSuccess'));
        navigate(`/trips/${newTrip.id}`);
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('errors.unknown'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold">
        {isEdit ? t('trips.editTrip') : t('trips.addNew')}
      </h1>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Trip Type Selector */}
        <div className="card">
          <TripTypeSelector
            value={tripType}
            onChange={handleTripTypeChange}
            disabled={isEdit}
          />
        </div>

        {/* Basic Info */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">{t('trips.basicInfo')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('trips.date')} <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                {...register('date', { required: t('validation.required') })}
                className="input-field"
              />
              {errors.date && (
                <p className="text-red-500 text-sm mt-1">{errors.date.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                {t('trips.vehicle')} <span className="text-red-500">*</span>
              </label>
              <Controller
                name="vehicleId"
                control={control}
                rules={{}}
                render={({ field }) => (
                  <SearchableCombobox
                    options={vehicleOptions}
                    value={field.value}
                    onChange={(val) => field.onChange(val as number | null)}
                    placeholder={t('trips.selectVehicle')}
                    required
                  />
                )}
              />
              {errors.vehicleId && (
                <p className="text-red-500 text-sm mt-1">{errors.vehicleId.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                {t('trips.distance')} ({t('trips.distanceUnit')})
              </label>
              <input
                type="number"
                step="0.1"
                {...register('distanceKm')}
                className="input-field"
                placeholder="150.5"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">{t('trips.driver')}</label>
              <Controller
                name="driverId"
                control={control}
                render={({ field }) => (
                  <SearchableCombobox
                    options={driverOptions}
                    value={field.value}
                    onChange={(val) => field.onChange(val as number | null)}
                    placeholder={t('trips.selectDriver')}
                  />
                )}
              />
            </div>

            {watch('driverId') && (
              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('trips.driverWage')} (VND)
                </label>
                <Controller
                  name="driverWage"
                  control={control}
                  render={({ field }) => (
                    <CurrencyInput
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="800.000"
                      className="input-field"
                    />
                  )}
                />
              </div>
            )}
          </div>
        </div>

        {/* Pickup Point */}
        <PickupDeliverySection
          label="pickup"
          control={control}
          data={pickup}
          onTypeChange={handlePickupTypeChange}
          onEntitySelect={handlePickupEntitySelect}
          suppliers={suppliers}
          customers={customers}
          warehouses={warehouses}
        />

        {/* Delivery Point */}
        <PickupDeliverySection
          label="delivery"
          control={control}
          data={delivery}
          onTypeChange={handleDeliveryTypeChange}
          onEntitySelect={handleDeliveryEntitySelect}
          suppliers={suppliers}
          customers={customers}
          warehouses={warehouses}
        />

        {/* Documents Upload */}
        <div className="card">
          <h2 className="text-lg font-semibold mb-4">{t('documents.tripDocuments')}</h2>
          <DocumentUpload
            entityType="trip"
            entityId={isEdit && id ? parseInt(id) : null}
            documentType="TRIP_DOCUMENT"
            disabled={loading}
            onUploadComplete={() => setDocumentRefreshKey(prev => prev + 1)}
          />
          
          {/* Show existing documents when editing */}
          {isEdit && id && (
            <div className="mt-4">
              <DocumentList
                entityType="trip"
                entityId={parseInt(id)}
                canDelete={true}
                refreshTrigger={documentRefreshKey}
              />
            </div>
          )}
        </div>

        {/* Items — conditional based on trip type */}
        {priceMode === 'both' ? (
          /* Chuyến trực tiếp SUPPLIER→CUSTOMER: hiển thị 2 danh sách riêng biệt */
          <div className="space-y-4">
            <div className="card">
              <h2 className="text-lg font-semibold mb-4">{t('trips.goods')}</h2>
              <div className="space-y-4">
                {/* Danh sách hàng nhập từ NCC */}
                <TripItemsTable
                  title="📦 Danh sách hàng nhập từ NCC"
                  accentColor="orange"
                  priceType="purchase"
                  fields={purchaseFields}
                  items={purchaseItems}
                  fieldArrayPath="purchaseItems"
                  control={control}
                  register={register}
                  onAppend={() => appendPurchase({ itemId: null, itemName: '', volumeWeight: '', unit: 'M3', unitPrice: '', purchasePrice: '', salePrice: '' })}
                  onRemove={removePurchase}
                  onItemSelect={handlePurchaseItemSelect}
                  itemOptions={itemOptions}
                />

                {/* Danh sách hàng giao cho KH */}
                <TripItemsTable
                  title="🚚 Danh sách hàng giao cho KH"
                  accentColor="blue"
                  priceType="sale"
                  fields={deliveryFields}
                  items={deliveryItems}
                  fieldArrayPath="deliveryItems"
                  control={control}
                  register={register}
                  onAppend={() => appendDelivery({ itemId: null, itemName: '', volumeWeight: '', unit: 'M3', unitPrice: '', purchasePrice: '', salePrice: '' })}
                  onRemove={removeDelivery}
                  onItemSelect={handleDeliveryItemSelect}
                  itemOptions={itemOptions}
                />
              </div>
            </div>
          </div>
        ) : (
          /* Chuyến nhập kho / giao hàng: danh sách đơn */
          <div className="card">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">{t('trips.items')}</h2>
              <button
                type="button"
                onClick={() =>
                  append({ itemId: null, itemName: '', volumeWeight: '', unit: 'M3', unitPrice: '', purchasePrice: '', salePrice: '', isGiftFromSupplier: false, isGiftToCustomer: false })
                }
                className="btn-secondary flex items-center text-sm"
              >
                <Plus className="w-4 h-4 mr-1" />
                {t('trips.addItem')}
              </button>
            </div>

            {/* Column Headers — ratio 3-1-1-2-2-2-1 */}
            <div className="grid grid-cols-12 gap-2 mb-1 px-0">
              <div className="col-span-4 text-xs font-medium text-gray-500">{t('trips.itemName')}</div>
              <div className="col-span-2 text-xs font-medium text-gray-500">{t('trips.volumeWeight')}</div>
              <div className="col-span-1 text-xs font-medium text-gray-500">{t('common.unit')}</div>
              {priceMode === 'purchase' ? (
                <div className="col-span-2 text-xs font-medium text-orange-500">{t('trips.purchasePrice')}</div>
              ) : priceMode === 'sale' ? (
                <div className="col-span-2 text-xs font-medium text-blue-500">{t('trips.salePrice')}</div>
              ) : (
                <div className="col-span-2 text-xs font-medium text-gray-500">{t('trips.unitPrice')}</div>
              )}
              <div className="col-span-2 text-xs font-medium text-gray-500 text-right">{t('trips.lineTotal')}</div>
              <div className="col-span-1"></div>
            </div>

            <div className="space-y-2">
              {fields.map((field, index) => (
                <div key={field.id} className="grid grid-cols-12 gap-2 items-center">
                  {/* Item Name */}
                  <div className="col-span-4">
                    <Controller
                      name={`items.${index}.itemId`}
                      control={control}
                      render={({ field: itemField }) => (
                        <SearchableCombobox
                          options={itemOptions}
                          value={itemField.value ?? null}
                          onChange={(val) => handleItemSelect(index, val)}
                          placeholder={t('trips.itemName')}
                        />
                      )}
                    />
                    <input type="hidden" {...register(`items.${index}.itemName`)} />
                  </div>

                  {/* Volume/Weight */}
                  <div className="col-span-2">
                    <input
                      type="number"
                      step="0.01"
                      placeholder="0"
                      {...register(`items.${index}.volumeWeight`)}
                      className="input-field"
                    />
                  </div>

                  {/* Unit */}
                  <div className="col-span-1">
                    <select {...register(`items.${index}.unit`)} className="input-field">
                      <option value="M3">{t('items.units.M3')}</option>
                      <option value="TON">{t('items.units.TON')}</option>
                      <option value="KG">{t('items.units.KG')}</option>
                      <option value="PIECE">{t('items.units.PIECE')}</option>
                    </select>
                  </div>

                  {/* Price inputs */}
                  {priceMode === 'purchase' ? (
                    <>
                      <div className="col-span-2">
                        {items[index]?.isGiftFromSupplier ? (
                          <div className="text-xs font-medium px-2 py-1.5 rounded border text-green-700 bg-green-50 border-green-200">
                            {t('trips.giftFree')}
                          </div>
                        ) : (
                          <Controller
                            name={`items.${index}.purchasePrice`}
                            control={control}
                            render={({ field }) => (
                              <CurrencyInput value={field.value} onChange={field.onChange} placeholder="0" className="input-field text-orange-700" />
                            )}
                          />
                        )}
                        <label className="flex items-center gap-1 mt-1 text-xs cursor-pointer text-green-600">
                          <input type="checkbox" {...register(`items.${index}.isGiftFromSupplier`)} className="rounded" />
                          {t('trips.giftFromSupplier')}
                        </label>
                      </div>
                      <input type="hidden" {...register(`items.${index}.unitPrice`)} />
                      <input type="hidden" {...register(`items.${index}.salePrice`)} />
                      <input type="hidden" {...register(`items.${index}.isGiftToCustomer`)} />
                    </>
                  ) : priceMode === 'sale' ? (
                    <>
                      <div className="col-span-2">
                        {items[index]?.isGiftToCustomer ? (
                          <div className="text-xs font-medium px-2 py-1.5 rounded border text-purple-700 bg-purple-50 border-purple-200">
                            {t('trips.giftFree')}
                          </div>
                        ) : (
                          <Controller
                            name={`items.${index}.salePrice`}
                            control={control}
                            render={({ field }) => (
                              <CurrencyInput value={field.value} onChange={field.onChange} placeholder="0" className="input-field text-blue-700" />
                            )}
                          />
                        )}
                        <label className="flex items-center gap-1 mt-1 text-xs cursor-pointer text-purple-600">
                          <input type="checkbox" {...register(`items.${index}.isGiftToCustomer`)} className="rounded" />
                          {t('trips.giftToCustomer')}
                        </label>
                      </div>
                      <input type="hidden" {...register(`items.${index}.unitPrice`)} />
                      <input type="hidden" {...register(`items.${index}.purchasePrice`)} />
                      <input type="hidden" {...register(`items.${index}.isGiftFromSupplier`)} />
                    </>
                  ) : (
                    <>
                      <div className="col-span-2">
                        <Controller
                          name={`items.${index}.unitPrice`}
                          control={control}
                          render={({ field }) => (
                            <CurrencyInput value={field.value} onChange={field.onChange} placeholder="0" className="input-field" />
                          )}
                        />
                      </div>
                      <input type="hidden" {...register(`items.${index}.purchasePrice`)} />
                      <input type="hidden" {...register(`items.${index}.salePrice`)} />
                    </>
                  )}

                  {/* Line Total */}
                  <div className="col-span-2 text-right text-xs">
                    {priceMode === 'purchase' ? (
                      items[index]?.isGiftFromSupplier
                        ? <span className="text-gray-400 font-medium">{t('trips.giftFree')}</span>
                        : <span className="text-orange-600 font-medium">{formatCurrency((parseFloat(items[index]?.volumeWeight) || 0) * (parseFloat(items[index]?.purchasePrice) || 0))}</span>
                    ) : priceMode === 'sale' ? (
                      items[index]?.isGiftToCustomer
                        ? <span className="text-gray-400 font-medium">{t('trips.giftFree')}</span>
                        : <span className="text-blue-600 font-medium">{formatCurrency((parseFloat(items[index]?.volumeWeight) || 0) * (parseFloat(items[index]?.salePrice) || 0))}</span>
                    ) : (
                      <span className="font-medium">
                        {formatCurrency((parseFloat(items[index]?.volumeWeight) || 0) * (parseFloat(items[index]?.unitPrice) || 0))}
                      </span>
                    )}
                  </div>

                  {/* Delete */}
                  <div className="col-span-1 flex justify-center">
                    <button
                      type="button"
                      onClick={() => remove(index)}
                      className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                      disabled={fields.length === 1}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Total */}
            <div className="mt-4 pt-4 border-t flex justify-end">
              <div className="text-right">
                <div className="text-sm text-gray-600">{t('trips.totalValue')}</div>
                <div className="text-xl font-bold text-primary-600">
                  {formatCurrency(totalValue)} đ
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Submit */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/trips')}
            className="btn-secondary"
          >
            {t('common.cancel')}
          </button>
          <button type="submit" disabled={loading} className="btn-primary">
            {loading ? t('common.loading') : isEdit ? t('common.save') : t('trips.addNew')}
          </button>
        </div>
      </form>
    </div>
  );
};
